import React, {createContext, useCallback, useContext, useMemo, useState} from "react";
import {useIsomorphicLayoutEffect} from "../utils/useIsomorphicLayoutEffect";
import {modeTokens, ThemeMode} from "./tokens";
import {resolveInitialMode, THEME_MODE_STORAGE_KEY} from "./resolveInitialMode";

/**
 * Light/dark mode state for the v3 design system.
 *
 * - Defaults to the OS preference (`prefers-color-scheme`) on first visit.
 * - An explicit user choice (toggle) is persisted to localStorage and wins over the OS preference.
 * - SSR/SSG-safe: the first render (and the static pre-render in Node) is always "light"; the real
 *   preference is resolved in an effect after hydration, so the pre-rendered markup never mismatches.
 *
 * The persisted key ({@link THEME_MODE_STORAGE_KEY}) and the precedence ({@link resolveInitialMode})
 * live in the React-free `resolveInitialMode.ts` so the pre-hydration `themeStamp.ts` bootstrap reuses
 * the identical decision instead of hand-duplicating it (ui#365).
 */

interface ThemeModeContextValue {
	mode: ThemeMode;
	setMode: (mode: ThemeMode) => void;
	toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/** True when the browser reports a dark-scheme preference. Safe to call anywhere (guards window). */
export function systemPrefersDark(): boolean {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
		return false;
	}
	return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** The RAW persisted string (or null when unavailable — private mode, SSR). Unvalidated. */
function rawStoredMode(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	try {
		return window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
	} catch {
		return null;
	}
}

/** The persisted mode, or null when unset/invalid/unavailable (private mode, SSR, …). */
export function readStoredMode(): ThemeMode | null {
	const stored = rawStoredMode();
	return stored === "light" || stored === "dark" ? stored : null;
}

/**
 * Resolves the effective mode: explicit user choice → OS preference → light. The precedence itself
 * lives in the dependency-free {@link resolveInitialMode} so the pre-hydration inline script in
 * `index.html` can duplicate the identical decision.
 */
export function resolvePreferredMode(): ThemeMode {
	return resolveInitialMode(rawStoredMode(), systemPrefersDark());
}

/**
 * Mirror the active mode onto the non-React document chrome so it always matches the in-app theme:
 * the `<html>` background + `color-scheme` (so the very next paint after a mode change shows the new
 * paper, never a flash of the old one), the `data-theme` attribute and the browser-UI `theme-color`
 * meta. The inline `<head>` script does
 * this once pre-hydration for the INITIAL mode (ui#144); this keeps it current on every later change
 * (the dark-mode toggle). No-op during SSG (no `document`).
 */
function applyModeToDocument(mode: ThemeMode): void {
	if (typeof document === "undefined") {
		return;
	}
	const paper = modeTokens[mode].paper;
	const root = document.documentElement;
	root.style.backgroundColor = paper;
	root.style.colorScheme = mode;
	root.dataset.theme = mode;
	const themeColorMeta = document.querySelector('meta[name="theme-color"]');
	if (themeColorMeta != null) {
		themeColorMeta.setAttribute("content", paper);
	}
}

function persistMode(mode: ThemeMode): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
	} catch {
		// Storage can be unavailable (private mode, blocked cookies) — the mode still works in-memory.
	}
}

interface ThemeModeProviderProps {
	children: React.ReactNode;
	/** Test/preview override; when set, preference resolution and persistence side effects are skipped. */
	initialMode?: ThemeMode;
}

export function ThemeModeProvider({children, initialMode}: ThemeModeProviderProps) {
	// Start light everywhere (including the SSG pre-render) and resolve the real preference on the
	// client only — this keeps the server and first client render identical (no hydration mismatch).
	const [mode, setModeState] = useState<ThemeMode>(initialMode ?? "light");

	// Resolve the real preference in a LAYOUT effect (ui#144): it still runs only on the client after
	// the first (light) render commits, so there is no hydration mismatch, but the corrective re-render
	// now happens BEFORE the browser paints the hydrated content — no white flash for dark-mode users.
	useIsomorphicLayoutEffect(() => {
		if (initialMode == null) {
			setModeState(resolvePreferredMode());
		}
	}, [initialMode]);

	// Keep the non-React document chrome (html background, color-scheme, data-theme, theme-color meta)
	// in lockstep with the active mode on every change — the initial resolution above AND the toggle.
	// The `data-theme` stamp also drives the CSS-variable bridge (AppTheme's static <style>): the dark
	// `--tf-*` block applies under `:root[data-theme="dark"]`, while light rides the `:root` defaults.
	useIsomorphicLayoutEffect(() => {
		applyModeToDocument(mode);
	}, [mode]);

	const setMode = useCallback((next: ThemeMode) => {
		setModeState(next);
		persistMode(next);
	}, []);

	const toggleMode = useCallback(() => {
		setMode(mode === "light" ? "dark" : "light");
	}, [mode, setMode]);

	const value = useMemo(() => ({mode, setMode, toggleMode}), [mode, setMode, toggleMode]);

	return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

/** Read + control the active theme mode. Must be rendered under a {@link ThemeModeProvider}. */
export function useThemeMode(): ThemeModeContextValue {
	const context = useContext(ThemeModeContext);
	if (context == null) {
		throw new Error("useThemeMode must be used within a ThemeModeProvider");
	}
	return context;
}
